import { PageLoadComplete } from '@west-australian-newspapers/page-lifecycle-provider'
import { Topic } from '../client-data-types/topic'
import { GtmEvent } from '../events/gtm-event-types'

import debug from 'debug'
import { RenderTarget } from '../context-providers'
import { DataLayerEventName } from '../helpers'
import { AuthenticationState, SubscriptionType } from '../data'
const chartbeatEventsDebug = debug('chartbeat-events')

let chartbeatInitialised = false

type ChartbeatUserType = 'anon' | 'lgdin' | 'paid'

type ChartbeatContentType =
    | 'article'
    | 'feature article'
    | 'sponsored article'
    | 'live blog'
    | 'photo gallery'
    | 'video'

export function getContentType({
    event,
}: {
    event: GtmEvent
}): ChartbeatContentType | null {
    const {
        publicationKind,
        articleKind,
        classification,
        layoutView,
        location,
        pageType,
    } = (event as PageLoadComplete)?.payload ?? {}

    if (
        pageType === 'publication' &&
        location?.pathname?.startsWith('/video/')
    ) {
        return 'video'
    }

    const rawContentType = publicationKind || articleKind
    if (!rawContentType) {
        return null
    }

    let contentType: ChartbeatContentType | null = null
    switch (rawContentType) {
        case 'article':
            contentType = 'article'
            break
        case 'feature-article':
            contentType = 'feature article'
            break
        case 'gallery':
            contentType = 'photo gallery'
            break
        case 'event':
            contentType = 'live blog'
            break
        case 'video':
            contentType = 'video'
            break
    }

    if (contentType === 'article' && layoutView === 'feature') {
        contentType = 'feature article'
    }

    if (contentType === 'article' && classification?.kind === 'sales') {
        contentType = 'sponsored article'
    }

    return contentType
}

export function getAuthors({ byline }: { byline: string | undefined }) {
    return (
        byline
            ?.replace(/\s+and\s+/gi, ',')
            .replace(/\s*&amp;\s*/g, ',')
            .replace(/\s*&\s*/g, ',') || ''
    )
}

export function getChartbeatDomain({
    hostname,
    fallback,
}: {
    hostname?: string | undefined
    fallback: string
}) {
    return (hostname || fallback).replace(/^www\./, '')
}

/**
 * Helper function to check if the domain is 7news.com.au or a sevennews dev/staging domain
 */
function is7NewsDomain(domain: string): boolean {
    // Match production domain
    if (domain === '7news.com.au') {
        return true
    }
    // Match dev/staging domains (e.g., sevennews.dev.swmdigital.io, sevennews-pr7004.dev.swmdigital.io)
    if (domain.includes('sevennews') && domain.includes('dev.swmdigital.io')) {
        return true
    }
    return false
}

/**
 * Helper function to strip query parameters from a URL path
 * For example: "/article?fbclid=123" -> "/article"
 */
function stripQueryParameters(path: string): string {
    const queryIndex = path.indexOf('?')
    return queryIndex >= 0 ? path.substring(0, queryIndex) : path
}

export function getChartbeatSections(props: Record<string, unknown>): string {
    if (!props) {
        return ''
    }

    if (!props || typeof props !== 'object') {
        return ''
    }

    const { primaryTopic, secondaryTopics } = props as {
        primaryTopic?: Topic
        secondaryTopics?: Topic[]
    }

    const topics: Topic[] = [
        ...(primaryTopic ? [primaryTopic] : []),
        ...(secondaryTopics || []),
    ]
        .filter((topic) => typeof topic === 'object')
        .filter((topic) => topic?.id)
        .filter(Boolean)

    // Get all parent topics
    // eg. wa/perth/central should return wa and wa/perth
    const parentTopics: string[] = []
    topics.forEach((topic) => {
        const topicSegments = topic?.id.split('/')
        for (let i = 0; i < topicSegments.length; i++) {
            i && parentTopics.push(topicSegments.slice(0, i).join('/'))
        }
    })

    if (topics.length > 0) {
        // add all the full topic paths
        let sections = topics.map((topic) => topic?.id)
        // add in the parent level paths
        sections = sections.concat(parentTopics)
        return (
            sections
                .reduce(
                    // remove duplicates
                    (acc: string[], cur: string) => [
                        ...acc,
                        ...(acc.indexOf(cur) === -1 ? [cur] : []),
                    ],
                    [],
                )
                .join(',') || ''
        )
    }

    const { pageType, location } = props as {
        pageType?: string
        location?: { pathname?: string }
    }

    if (
        pageType &&
        ['topic', 'static', 'video'].includes(pageType) &&
        location?.pathname
    ) {
        return location.pathname.replace(/^\//, '')
    }

    return ''
}

export function handleChartbeatTracking(
    event: GtmEvent,
    chartbeatId: number,
    publicHostname: string,
    renderTarget: string,
    authState: AuthenticationState,
    headlineTestingEnabled: boolean,
    conversionsEnabled: boolean,
    videoTrackingEnabled: boolean,
) {
    chartbeatEventsDebug(
        'handleChartbeatTracking called with event: %o, chartbeatId: %o, publicHostname: %o, renderTarget: %o, authState: %o',
        event,
        chartbeatId,
        publicHostname,
        renderTarget,
        authState,
    )

    const userType = getChartbeatUserType(event, authState)
    if (userType !== null) {
        setChartbeatUserType(userType)
    }

    // Re-add the scripts, don't worry about updating the chartbeat config
    if (event.type === DataLayerEventName.bonsaiTruskinRendered) {
        addScripts(
            headlineTestingEnabled,
            conversionsEnabled,
            videoTrackingEnabled,
        )
        // New page load, initialise with the new configs
    } else if (event.type === 'page-load-complete') {
        const { location, title, byline, hostname } = event.payload

        const contentType = getContentType({ event })
        const path = location.pathname
        const chartbeatSections = getChartbeatSections(event.payload)
        const authors = getAuthors({ byline })
        const sponsorName = contentType === 'sponsored article' ? byline : null
        const isMobileApp = (renderTarget as RenderTarget) === 'app'
        const chartbeatDomain = getChartbeatDomain({
            hostname,
            fallback: publicHostname,
        })

        // Initialise Chartbeat if it hasn't been initialised yet, or if the truskin
        // ad unit is rendered.
        if (!chartbeatInitialised) {
            initChartbeat(
                chartbeatId,
                chartbeatDomain,
                authors,
                chartbeatSections,
                contentType,
                sponsorName,
                isMobileApp,
                headlineTestingEnabled,
                conversionsEnabled,
                videoTrackingEnabled,
                path,
            )
        } else {
            trackChartbeatPageview(
                title,
                chartbeatDomain,
                byline,
                chartbeatSections,
                path,
                contentType,
                sponsorName,
                isMobileApp,
            )
        }
    }
}

export function initChartbeat(
    id: number,
    domain: string,
    authors: string,
    sections: string,
    contentType: ChartbeatContentType | null,
    sponsorName: string | null,
    isMobileApp: boolean | null,
    isChartbeatHeadlineTestingEnabled: boolean,
    conversionsEnabled: boolean,
    videoTrackingEnabled: boolean,
    path?: string,
) {
    chartbeatEventsDebug(
        'initChartbeat called with id: %o, domain: %o, authors: %o, sections: %o, contentType: %o, sponsorName: %o, isMobileApp: %o, isChartbeatHeadlineTestingEnabled: %o, videoTrackingEnabled: %o, path: %o',
        id,
        domain,
        authors,
        sections,
        contentType,
        sponsorName,
        isMobileApp,
        isChartbeatHeadlineTestingEnabled,
        videoTrackingEnabled,
        path,
    )

    const _sf_async_config: ChartbeatAsyncConfig = (window._sf_async_config =
        window._sf_async_config || {})
    _sf_async_config.uid = id
    _sf_async_config.domain = domain
    _sf_async_config.useCanonical = true
    _sf_async_config.useCanonicalDomain = false
    _sf_async_config.sections = sections
    _sf_async_config.authors = authors
    _sf_async_config.flickerControl = false

    if (is7NewsDomain(domain)) {
        // Set useCanonical and useCanonicalDomain to false for 7news.com.au
        _sf_async_config.useCanonical = false

        // Set custom path with query parameters stripped
        // Per Chartbeat docs, path should include domain (e.g., "7news.com.au/news/article")
        if (path) {
            const cleanPath = stripQueryParameters(path)
            _sf_async_config.path = `${domain}${cleanPath}`
            chartbeatEventsDebug(
                '7NEWS: Setting custom path without query params: %o (original: %o)',
                _sf_async_config.path,
                path,
            )
        }
    }

    if (isMobileApp !== null) {
        _sf_async_config.mobileApp = isMobileApp
    }

    if (contentType) {
        _sf_async_config.type = contentType
    }

    if (sponsorName) {
        _sf_async_config.sponsorName = sponsorName
    }

    // note: on initial pageload, we let chartbeat derive the title
    // from the `<title>` element on the page

    addScripts(
        isChartbeatHeadlineTestingEnabled,
        conversionsEnabled,
        videoTrackingEnabled,
    )
}

export function addScripts(
    isChartbeatHeadlineTestingEnabled: boolean,
    conversionsEnabled = false,
    videoTrackingEnabled = false,
) {
    addScript(videoTrackingEnabled) // chartbeat.js or chartbeat_video.js

    if (conversionsEnabled) {
        addSubscriptionsScript() // subscriptions.js (conversion API)
    }

    if (isChartbeatHeadlineTestingEnabled) {
        addHeadlineTestingScript()
    }

    chartbeatInitialised = true
}

export function addScript(videoTrackingEnabled = false) {
    const e = document.createElement('script')
    const n = document.getElementsByTagName('script')[0]
    e.type = 'text/javascript'
    e.async = true
    // Use chartbeat_video.js for video engagement tracking (includes all standard functionality)
    e.src = videoTrackingEnabled
        ? '//static.chartbeat.com/js/chartbeat_video.js'
        : '//static.chartbeat.com/js/chartbeat.js'
    n.parentNode?.insertBefore(e, n)
}
export function addHeadlineTestingScript() {
    const e = document.createElement('script')
    const n = document.getElementsByTagName('title')[0] // Load after page title
    e.type = 'text/javascript'
    e.async = true
    e.src = '//static.chartbeat.com/js/chartbeat_mab.js'
    n.parentNode?.insertBefore(e, n.nextSibling)
}

// NEW: load subscriptions.js to enable conversion events
export function addSubscriptionsScript() {
    if (
        document.querySelector(
            'script[src*="static.chartbeat.com/js/subscriptions.js"]',
        )
    ) {
        return
    }
    const s = document.createElement('script')
    const n = document.getElementsByTagName('script')[0]
    s.type = 'text/javascript'
    s.async = true
    s.src = 'https://static.chartbeat.com/js/subscriptions.js'
    n.parentNode?.insertBefore(s, n)
}

export function trackChartbeatPageview(
    title: string,
    domain: string,
    authors: string,
    sections: string,
    path: string,
    contentType: ChartbeatContentType | null,
    sponsorName: string | null,
    isMobileApp: boolean | null,
) {
    chartbeatEventsDebug(
        'trackChartbeatPageView called with title: %o, domain: %o, authors: %o, sections: %o, path: %o, contentType: %o, sponsorName: %o, isMobileApp: %o',
        title,
        domain,
        authors,
        sections,
        path,
        contentType,
        sponsorName,
        isMobileApp,
    )

    if (typeof pSUPERFLY !== 'undefined') {
        const is7News = is7NewsDomain(domain)
        // For 7news.com.au, strip query parameters from the path and include domain
        const cleanPath = is7News ? domain + stripQueryParameters(path) : path

        if (is7News) {
            chartbeatEventsDebug(
                '7NEWS: Tracking pageview with clean path: %o (original: %o)',
                cleanPath,
                path,
            )
        }

        pSUPERFLY.virtualPage({
            domain,
            sections: sections || '',
            authors: authors || '',
            path: cleanPath || '',
            title: title || '',
            type: contentType || '',
            sponsorName: sponsorName || '',
            mobileApp: isMobileApp || false,
        })
    }
}

export function getChartbeatUserType(
    event: GtmEvent,
    authState: AuthenticationState,
): ChartbeatUserType | null {
    chartbeatEventsDebug(
        'getChartbeatUserType called with event: %o, authState: %o',
        event,
        authState,
    )

    if (event.type === DataLayerEventName.authLogout) {
        chartbeatEventsDebug(
            `getChartbeatUserType event:${event.type} => "anon"`,
        )
        return 'anon'
    }

    if (
        event.type === DataLayerEventName.authLogin ||
        event.type === DataLayerEventName.authLoginRefresh ||
        event.type === 'page-load-complete'
    ) {
        chartbeatEventsDebug(
            `getChartbeatUserType event:${event.type} => authstate: .subscriptionType: %o, .isLoggedIn: %o`,
            authState?.subscriptionType,
            authState?.isLoggedIn,
        )

        const subscriptionType: SubscriptionType | undefined =
            authState?.subscriptionType

        switch (subscriptionType) {
            case 'subscriber':
                return 'paid'
            case 'corporate':
                return 'paid'
            case 'corporate-school':
                return 'paid'
            case 'corporate-site':
                return 'paid'
            case 'swg':
                return 'paid'
            case 'none':
                // fall through
                break
        }

        const isLoggedIn: boolean | undefined = authState.isLoggedIn

        if (isLoggedIn) {
            return 'lgdin'
        }

        if (event.type === 'page-load-complete') {
            return 'anon'
        }
    }

    return null
}

/**
 * Chartbeat looks for a global _cbq variable containing user data
 * @param userType
 */
export function setChartbeatUserType(userType: ChartbeatUserType) {
    chartbeatEventsDebug(
        'setChartbeatUserType called with userType: %o',
        userType,
    )
    const _cbq = (window._cbq = window._cbq || [])
    _cbq.push(['_acct', userType])
}

// ============================
// Chartbeat micro-conversions
// ============================

type CbConversionEvent =
    | 'trackSearchComplete'
    | 'trackNewsletterSignupStart'
    | 'trackNewsletterSignupShown'
    | 'trackNewsletterSignupComplete'
    | 'trackNewsletterSignupCancel'
    | 'trackUnsubscribeShown'
    | 'trackUnsubscribeStart'
    | 'trackUnsubscribeComplete'
    | 'trackUnsubscribeCancel'

type CbParams = {
    eventLabel?: 'Site Search' | 'Newsletters'
    pathOverride?: string
    eventValue?: number
}

declare global {
    interface Window {
        chartbeat?: (eventName: string, params?: CbParams) => void
    }
}

/** Fire only after subscriptions.js exposes chartbeat(). */
function whenChartbeatReady(cb: () => void, timeoutMs = 8000): void {
    const t0 = Date.now()
    if (typeof window.chartbeat === 'function') {
        cb()
        return
    }
    const id = window.setInterval(() => {
        if (typeof window.chartbeat === 'function') {
            window.clearInterval(id)
            cb()
        } else if (Date.now() - t0 > timeoutMs) {
            window.clearInterval(id)
        }
    }, 100)
}

function sendCbEvent(eventName: CbConversionEvent, params?: CbParams): void {
    whenChartbeatReady(() => {
        try {
            window.chartbeat?.(eventName, params)
            chartbeatEventsDebug(
                'CB conversion fired: %s %o',
                eventName,
                params || {},
            )
        } catch (e) {
            chartbeatEventsDebug('CB conversion error: %o', e)
        }
    })
}

export type TrackSearchOptions = {
    path: string
    resultsCount?: number
}

export function cbTrackSearchComplete(opts: TrackSearchOptions): void {
    const params: CbParams = {
        eventLabel: 'Site Search',
        pathOverride: opts.path,
    }
    if (typeof opts.resultsCount === 'number') {
        params.eventValue = opts.resultsCount
    }
    sendCbEvent('trackSearchComplete', params)
}

export function cbTrackNewsletterSignupStart(): void {
    sendCbEvent('trackNewsletterSignupStart', { eventLabel: 'Newsletters' })
}
export function cbTrackNewsletterSignupShown(): void {
    sendCbEvent('trackNewsletterSignupShown', { eventLabel: 'Newsletters' })
}
export function cbTrackNewsletterSignupComplete(): void {
    sendCbEvent('trackNewsletterSignupComplete', { eventLabel: 'Newsletters' })
}
export function cbTrackNewsletterSignupCancel(): void {
    sendCbEvent('trackNewsletterSignupCancel', { eventLabel: 'Newsletters' })
}
export function cbTrackUnsubscribeShown(): void {
    sendCbEvent('trackUnsubscribeShown', { eventLabel: 'Newsletters' })
}
export function cbTrackUnsubscribeStart(): void {
    sendCbEvent('trackUnsubscribeStart', { eventLabel: 'Newsletters' })
}
export function cbTrackUnsubscribeComplete(): void {
    sendCbEvent('trackUnsubscribeComplete', { eventLabel: 'Newsletters' })
}
export function cbTrackUnsubscribeCancel(): void {
    sendCbEvent('trackUnsubscribeCancel', { eventLabel: 'Newsletters' })
}

export function handleChartbeatConversionEvent(evt: {
    type:
        | 'search'
        | 'newsletterSignupStart'
        | 'newsletterSignupShown'
        | 'newsletterSignupComplete'
        | 'newsletterSignupCancel'
        | 'unsubscribeShown'
        | 'unsubscribeStart'
        | 'unsubscribeComplete'
        | 'unsubscribeCancel'
    resultsCount?: number
    path?: string
}): void {
    switch (evt.type) {
        case 'search':
            cbTrackSearchComplete({
                path: evt.path || '/search',
                resultsCount: evt.resultsCount,
            })
            break
        case 'newsletterSignupStart':
            cbTrackNewsletterSignupStart()
            break
        case 'newsletterSignupShown':
            cbTrackNewsletterSignupShown()
            break
        case 'newsletterSignupComplete':
            cbTrackNewsletterSignupComplete()
            break
        case 'newsletterSignupCancel':
            cbTrackNewsletterSignupCancel()
            break
        case 'unsubscribeShown':
            cbTrackUnsubscribeShown()
            break
        case 'unsubscribeStart':
            cbTrackUnsubscribeStart()
            break
        case 'unsubscribeComplete':
            cbTrackUnsubscribeComplete()
            break
        case 'unsubscribeCancel':
            cbTrackUnsubscribeCancel()
            break
    }
}
